import React, { useState, useRef } from 'react';
import { Upload as UploadIcon, Package, ShoppingCart, Truck, AlertCircle, Loader2, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DataTypeOption {
  id: 'products' | 'sales' | 'purchases';
  title: string;
  description: string;
  icon: React.ReactNode;
}

const dataTypes: DataTypeOption[] = [
  {
    id: 'products',
    title: 'Productos',
    description: 'Información detallada de productos (nombre, código, stock, etc.)',
    icon: <Package className="w-8 h-8" />,
  },
  {
    id: 'sales',
    title: 'Ventas',
    description: 'Detalles de transacciones de venta (fecha, producto, cantidad, precio de venta, etc.)',
    icon: <ShoppingCart className="w-8 h-8" />,
  },
  {
    id: 'purchases',
    title: 'Compras',
    description: 'Información de compra de mercadería (fecha, producto, cantidad, proveedor, precio de compra, etc.)',
    icon: <Truck className="w-8 h-8" />,
  },
];

export const DataUpload = () => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    const validTypes = ['.xlsx', '.csv'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return validTypes.includes(fileExtension);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Solo se permiten archivos Excel (.xlsx) o CSV (.csv)');
      }
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Solo se permiten archivos Excel (.xlsx) o CSV (.csv)');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedType || !file) return;
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    setShowConfirmation(false);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      // Upload file to Supabase
      const timestamp = new Date().getTime();
      const filePath = `${user.id}/${selectedType}/${timestamp}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('archivos_db')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Construct the signed URL
      const signedUrl = `https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/authenticated/archivos_db/${filePath}`;

      // Send webhook
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/0f578eac-ee18-4293-a4ef-3c47d4094464', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          db_type: selectedType.toLowerCase(),
          db_url: signedUrl,
        }),
      });

      const data = await response.json();

      if (data.response === 'error') {
        throw new Error(data.description);
      }

      setSuccess(true);
      setFile(null);
      setSelectedType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      setError(error instanceof Error ? error.message : 'Error al procesar el archivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8 flex items-center gap-3">
        <UploadIcon className="w-8 h-8 text-gray-700" />
        <h1 className="text-2xl font-semibold text-gray-900">Subir Datos</h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
          Archivo procesado exitosamente
        </div>
      )}

      {/* Data Type Selection */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Tipo de Datos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {dataTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`p-6 rounded-lg border-2 transition-all ${
                selectedType === type.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-200'
              }`}
            >
              <div className={`mb-4 ${selectedType === type.id ? 'text-blue-500' : 'text-gray-500'}`}>
                {type.icon}
              </div>
              <h3 className="text-lg font-medium mb-2">{type.title}</h3>
              <p className="text-sm text-gray-500">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Archivo</h2>
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={handleFileInput}
            className="hidden"
          />
          
          {file ? (
            <div className="flex items-center justify-center gap-4">
              <span className="text-gray-700">{file.name}</span>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-gray-500 hover:text-red-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div>
              <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Arrastra y suelta tu archivo aquí o
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-blue-500 hover:text-blue-600 ml-1"
                >
                  selecciona un archivo
                </button>
              </p>
              <p className="text-sm text-gray-500">Solo archivos Excel (.xlsx) o CSV (.csv)</p>
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowConfirmation(true)}
          disabled={!selectedType || !file || loading}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <UploadIcon className="w-5 h-5" />
          )}
          Subir Archivo
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar Subida</h3>
            <div className="mb-6">
              <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                Por favor, verifica que el archivo seleccionado sea correcto. Los datos incorrectos pueden afectar la base de datos de la empresa.
              </p>
              <p className="text-sm text-gray-500">
                Recomendamos revisar el archivo antes de proceder con la subida.
              </p>
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Proceder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};